import { t } from 'i18next';

import {
  mapDeviceHealthReason,
  type DeviceHealthMessage,
  type RppgAbortReasonCode,
} from 'lib/medsi/device-health';
import type { RppgQualityReasonCode } from 'services/rppg/api';

export type { RppgAbortReasonCode } from 'lib/medsi/device-health';

export type RppgQualityErrorMsg = DeviceHealthMessage;

function humanizeReasonCode(code: string): string {
  return code
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function mapRppgQualityReason(
  code: RppgAbortReasonCode,
): RppgQualityErrorMsg {
  const deviceMapped = mapDeviceHealthReason(code);
  if (deviceMapped) {
    return deviceMapped;
  }

  switch (code as RppgQualityReasonCode) {
    case 'POOR_LIGHTING':
      return {
        header: t('medsi_quality_poor_lighting_title'),
        description: t('medsi_quality_poor_lighting_message'),
      };
    case 'HOLD_STILL':
      return {
        header: t('medsi_quality_hold_still_title'),
        description: t('medsi_quality_hold_still_message'),
      };
    case 'WEAK_SIGNAL':
      return {
        header: t('medsi_quality_weak_signal_title'),
        description: t('medsi_quality_weak_signal_message'),
      };
    case 'FACE_NOT_VISIBLE':
      return {
        header: t('medsi_quality_face_not_visible_title'),
        description: t('medsi_quality_face_not_visible_message'),
      };
    case 'LOW_FRAME_RATE':
      return {
        header: t('medsi_device_low_frame_rate_title'),
        description: t('medsi_device_low_frame_rate_message'),
      };
    default:
      return {
        header: humanizeReasonCode(String(code)),
        description: t('medsi_quality_abort_intro'),
      };
  }
}

export function mapRppgQualityReasons(
  reasons: RppgAbortReasonCode[] | null | undefined,
): RppgQualityErrorMsg[] {
  if (!reasons?.length) {
    return [
      {
        header: t('error'),
        description: t('medsi_quality_abort_intro'),
      },
    ];
  }

  const seen = new Set<string>();
  const mapped: RppgQualityErrorMsg[] = [];
  for (const code of reasons) {
    const key = String(code);
    if (seen.has(key)) continue;
    seen.add(key);
    mapped.push(mapRppgQualityReason(code));
  }
  return mapped;
}
